import { useLazyQuery } from "@apollo/client";
import { IonCol, IonGrid, IonRow } from "@ionic/react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import Button from "@components/buttons/Button";
import DeleteConfirmationAlert from "@components/DeleteConfirmationAlert";
import styles from "@components/expert/ExpertDashboardTasks.module.css";
import TaskForm from "@components/forms/TaskForm";
import HandleQuery from "@components/HandleQuery";
import Modal from "@components/Modal";
import Table, { TableHeader } from "@components/Table";
import TaskTableRow from "@components/TaskTableRow";
import IconBase from "@components/text/IconBase";
import LargeText from "@components/text/LargeText";
import useSubscribeToExpertTasks from "@hooks/subscriptions/useSubscribeToExpertTasks";
import useMutationWithValidation from "@hooks/useMutationWithValidation";
import useNodesFromConnection from "@hooks/useNodesFromConnection";
import useOpener from "@hooks/useOpener";
import {
  ExpertTasksSortByEnum,
  GetExpertTasksDocument,
  GetExpertTasksQuery,
  GetExpertTasksQueryVariables,
  GetTaskSummaryDocument,
  GetTaskSummaryQuery,
  GetTaskSummaryQueryVariables,
  TaskFragment as Task,
  TaskDestroyDocument,
  TaskDestroyMutation,
  TaskDestroyMutationVariables,
  TaskStatusEnum,
  TaskToggleCompletedDocument,
  TaskToggleCompletedMutation,
  TaskToggleCompletedMutationVariables
} from "@typing/Generated";

type Props = {
  expertId: string;
};

const ExpertDashboardTasks = ({ expertId }: Props) => {
  const intl = useIntl();
  const deleteTaskOpener = useOpener();
  const taskOpener = useOpener();

  const [editingTask, setEditingTask] = useState<Task | null>(null);
  const [dashboardFilter, setDashboardFilter] = useState<TaskStatusEnum>(TaskStatusEnum.INCOMPLETE);

  const endTime = useMemo(() => {
    const end = new Date();
    end.setDate(end.getDate() + 3);
    end.setHours(23, 59, 59, 999);
    return end;
  }, []);

  const [destroyTask] = useMutationWithValidation<TaskDestroyMutation, TaskDestroyMutationVariables>(
    TaskDestroyDocument,
    "task"
  );

  const [toggleTaskCompleted] = useMutationWithValidation<
    TaskToggleCompletedMutation,
    TaskToggleCompletedMutationVariables
  >(TaskToggleCompletedDocument, "task");

  const handleTaskDestroy = useCallback(() => {
    if (editingTask) {
      destroyTask({
        id: editingTask.id
      });
    }
  }, [editingTask, destroyTask]);

  const handleTaskCreate = useCallback(() => {
    setEditingTask(null);
    taskOpener.open();
  }, [taskOpener]);

  const [getTasks, getTasksQuery] = useLazyQuery<GetExpertTasksQuery, GetExpertTasksQueryVariables>(
    GetExpertTasksDocument
  );

  const [getTaskSummary, getTaskSummaryQuery] = useLazyQuery<GetTaskSummaryQuery, GetTaskSummaryQueryVariables>(
    GetTaskSummaryDocument
  );

  const handleTaskToggleCompleted = useCallback(
    (task: Task | null) => {
      if (task) {
        toggleTaskCompleted({
          completed: !task.completed,
          id: task.id
        });
      }
    },
    [toggleTaskCompleted]
  );

  useSubscribeToExpertTasks(expertId ?? "", () => {
    getTasksQuery.refetch();
    getTaskSummaryQuery.refetch();
  });

  useEffect(() => {
    getTasks({
      variables: {
        endTime,
        expertId,
        status: dashboardFilter
      }
    });
  }, [dashboardFilter, endTime, getTasks, expertId]);

  useEffect(() => {
    getTaskSummary({
      variables: {
        expertId
      }
    });
  }, [expertId, getTaskSummary]);

  const tasks = useNodesFromConnection(getTasksQuery.data?.expertTasks);

  const handleFilterChange = useCallback(
    (value: TaskStatusEnum) => {
      if (dashboardFilter === value) {
        setDashboardFilter(TaskStatusEnum.INCOMPLETE);
      } else {
        setDashboardFilter(value);
      }
    },
    [dashboardFilter]
  );

  useEffect(() => {
    if (!getTasksQuery.loading && getTasksQuery.data?.expertTasks.pageInfo.hasNextPage) {
      getTasksQuery.fetchMore({ variables: { cursor: getTasksQuery.data?.expertTasks.pageInfo.endCursor ?? "" } });
    }
  }, [getTasksQuery]);

  const tableHeaders: TableHeader<ExpertTasksSortByEnum>[] = [
    { messageKey: "pages.expert.tasks.tableHeadings.taskTitle" },
    { messageKey: "pages.expert.tasks.tableHeadings.yourRole" },
    { messageKey: "pages.expert.tasks.tableHeadings.assignee" },
    { messageKey: "pages.expert.tasks.tableHeadings.client" },
    { messageKey: "pages.expert.tasks.tableHeadings.lovedOne" },
    { messageKey: "pages.expert.tasks.tableHeadings.clientVisible" },
    { messageKey: "pages.expert.tasks.tableHeadings.dueDate" }
  ];

  return (
    <HandleQuery query={getTasksQuery}>
      {_data => (
        <HandleQuery query={getTaskSummaryQuery}>
          {taskSummaryData => (
            <>
              <IonGrid>
                <IonRow className="ion-align-items-center">
                  <IonCol>
                    <div className={styles.headerTitle}>
                      <LargeText>
                        <FormattedMessage id="pages.expert.dashboard.boxHeadings.taskList" />
                      </LargeText>
                    </div>
                  </IonCol>
                  <IonCol size="auto">
                    <Button fill="clear" onClick={handleTaskCreate}>
                      <IconBase icon="add" slot="start" />
                      <FormattedMessage id="pages.expert.dashboard.addTask" />
                    </Button>
                  </IonCol>
                  <IonCol size="auto">
                    <Button fill="outline" routerLink={`/expert/tasks?expertId=${expertId}`}>
                      <FormattedMessage id="pages.expert.dashboard.viewAllTasks" />
                    </Button>
                  </IonCol>
                </IonRow>
              </IonGrid>
              <IonGrid>
                <IonRow>
                  <IonCol size="auto">
                    <div
                      className={`${styles.filter} ${dashboardFilter === TaskStatusEnum.TODAY ? styles.selected : ""}`}
                      onClick={() => handleFilterChange(TaskStatusEnum.TODAY)}
                    >
                      <FormattedMessage
                        id="pages.expert.dashboard.taskFilters.today"
                        values={{ count: taskSummaryData.taskSummary.today }}
                      />
                    </div>
                  </IonCol>
                  <IonCol size="auto">
                    <div
                      className={`${styles.filter} ${
                        dashboardFilter === TaskStatusEnum.COMPLETED_TODAY ? styles.selected : ""
                      }`}
                      onClick={() => handleFilterChange(TaskStatusEnum.COMPLETED_TODAY)}
                    >
                      <FormattedMessage
                        id="pages.expert.dashboard.taskFilters.completedToday"
                        values={{ count: taskSummaryData.taskSummary.completedToday }}
                      />
                    </div>
                  </IonCol>
                  <IonCol size="auto">
                    <div
                      className={`${styles.filter} ${
                        dashboardFilter === TaskStatusEnum.TOMORROW ? styles.selected : ""
                      }`}
                      onClick={() => handleFilterChange(TaskStatusEnum.TOMORROW)}
                    >
                      <FormattedMessage
                        id="pages.expert.dashboard.taskFilters.tomorrow"
                        values={{ count: taskSummaryData.taskSummary.tomorrow }}
                      />
                    </div>
                  </IonCol>
                </IonRow>
              </IonGrid>
              <Table<ExpertTasksSortByEnum>
                fetchMore={getTasksQuery.fetchMore}
                headerKey="pages.expert.tasks.tableHeadings"
                headers={tableHeaders}
                pageInfo={getTasksQuery.data?.expertTasks.pageInfo}
                skipScroll
              >
                {tasks.map(task => (
                  <TaskTableRow
                    handleTaskEdit={t => {
                      setEditingTask(t);
                      taskOpener.open();
                    }}
                    handleTaskToggleCompleted={handleTaskToggleCompleted}
                    hiddenDate
                    key={task.id}
                    task={task}
                  />
                ))}
              </Table>

              <Modal opener={taskOpener} title={intl.formatMessage({ id: "forms.task.addTask" })}>
                {({ modalDisplayed }) => (
                  <TaskForm
                    autofocus={modalDisplayed}
                    onCancel={taskOpener.close}
                    onSuccess={taskOpener.close}
                    refetch={getTasksQuery.refetch}
                    task={editingTask}
                  />
                )}
              </Modal>

              <DeleteConfirmationAlert
                messageKey="pages.expert.tasks.areYouSure"
                messageValue={
                  editingTask
                    ? editingTask.title ?? intl.formatMessage({ id: `models.tasks.kind.${editingTask?.kind ?? ""}` })
                    : ""
                }
                messageValueKey="title"
                onDelete={() => handleTaskDestroy()}
                opener={deleteTaskOpener}
              />
            </>
          )}
        </HandleQuery>
      )}
    </HandleQuery>
  );
};

export default ExpertDashboardTasks;
